import axios from 'axios'
import type { AxiosError } from 'axios'
import { getAuthToken, getAuthEpoch, handleAuthFailure } from '@features/auth/tokenBridge'
import {
  PERMISSION_BYPASS_HEADER,
  permissionBypassHeaders,
  usePermissionBypassStore,
} from '@features/permissions/stores/usePermissionBypassStore'
import { env } from '@/config/env'
import { APP_NAME } from '@/constants'
import { markAppUpdating } from '../stores/useAppUpdateStore'
import { UPDATING_CODE, isUpdatingResponse } from './retryPolicy'

const API_BASE_URL = env.VITE_API_URL

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

const requestEpochs = new WeakMap<object, number>()
function assertCurrentSession(config: object) {
  const epoch = requestEpochs.get(config)
  if (epoch !== undefined && epoch !== getAuthEpoch())
    throw new axios.CanceledError('Authentication changed')
}

const PUBLIC_ENDPOINTS = ['/health']

function isPublicRequest(url?: string): boolean {
  return PUBLIC_ENDPOINTS.some((endpoint) => url?.includes(endpoint))
}

/** Server replies that say nothing a person can act on (the framework's defaults among them). */
const UNHELPFUL_SERVER_MESSAGES = new Set([
  'Internal server error',
  'Internal Server Error',
  'Not Found',
  'Method Not Allowed',
])

/** What a failed request means, when the server didn't say. */
function messageForStatus(status: number): string {
  if (status === 401) return 'Your session has ended. Sign in again.'
  if (status === 403) return "You don't have permission to do that."
  if (status === 404) return "That doesn't exist or was removed."
  if (status === 409) return 'That conflicts with a recent change. Refresh and try again.'
  if (status === 400 || status === 422)
    return "That didn't work. Check what you entered and try again."
  return DEFAULT_ERROR_MESSAGE
}

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Try again.'

/** What a request turned away while the app updates says, should anything show it. */
export const APP_UPDATING_MESSAGE = `${APP_NAME} is updating. The app will reload automatically when the update is complete.`

/**
 * What to tell a person about something that failed: the message of an error
 * from the API client (the server's sentence, or one for the status; see the
 * response interceptor) or of a UserFacingError, and `fallback` for anything
 * else, whose message is for developers (a TypeError's, a library's).
 */
export function getErrorMessage(error: unknown, fallback = DEFAULT_ERROR_MESSAGE): string {
  if (error instanceof UserFacingError) return error.message
  if (!(error instanceof Error) || !error.message) return fallback
  const { code, status } = error as Error & { code?: unknown; status?: unknown }
  const isFromApiClient =
    (typeof status === 'number' && typeof code === 'string') || code === 'network_error'
  return isFromApiClient ? error.message : fallback
}

/** What the API client rejects with when the server answered with an error. */
export type ApiError = Error & {
  code: string
  status: number
  errorId?: string
  details?: Record<string, unknown>
}

/** The id the server logged a failed request under, for support to look up. */
export function getErrorId(error: unknown): string | null {
  const errorId = (error as { errorId?: unknown } | null)?.errorId
  return typeof errorId === 'string' ? errorId : null
}

/** An error thrown with a sentence meant for the person using the app. */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UserFacingError'
  }
}

export function isPermissionDeniedError(error: unknown): boolean {
  return (
    error instanceof Error &&
    'code' in error &&
    (error as Error & { code?: string }).code === 'permission_denied'
  )
}

/**
 * Whether a request for the record a page's address names failed because
 * that record isn't there: 404, or 422 for an id that can't be one (a typo,
 * a made-up link). The client puts the response's status on its errors.
 */
export function isMissingRecordError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status
  return status === 404 || status === 422
}

// Request interceptor
apiClient.interceptors.request.use(async (config) => {
  requestEpochs.set(config, getAuthEpoch())
  if (!isPublicRequest(config.url)) {
    const token = await getAuthToken()

    assertCurrentSession(config)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // An administrator's Bypass Permissions switch (the Me menu).
    Object.assign(config.headers, permissionBypassHeaders())
  }

  return config
})

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    assertCurrentSession(response.config)
    return response
  },
  async (
    error: AxiosError<{
      detail?: string
      code?: string
      error_id?: string
      details?: Record<string, unknown>
      error?: { code?: string; message?: string }
    }>
  ) => {
    const originalRequest = error.config

    if (!originalRequest) {
      return Promise.reject(error)
    }

    assertCurrentSession(originalRequest)
    if (axios.isCancel(error)) return Promise.reject(error)

    if (!error.response) {
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
      const message = timedOut
        ? 'The server took too long to respond. Try again.'
        : 'Unable to reach the server. Check your connection and try again.'
      const networkError = new Error(message) as Error & { code: string; timedOut: boolean }
      networkError.code = 'network_error'
      // A server that never answered differs from one that couldn't be
      // reached (retryPolicy.ts waits out the second, not the first).
      networkError.timedOut = timedOut
      return Promise.reject(networkError)
    }

    const data = error.response.data

    // A deploy is holding the app: the updating modal covers the page and
    // reloads it onto the new build when the update is over. Any request can
    // meet it, saves included, so the store is set here for all of them.
    if (isUpdatingResponse(error.response.status, data)) {
      markAppUpdating(data)
      const updatingError = new Error(APP_UPDATING_MESSAGE) as Error & {
        code: string
        status: number
      }
      updatingError.code = UPDATING_CODE
      updatingError.status = error.response.status
      return Promise.reject(updatingError)
    }

    const isAuthenticatedRequest =
      !isPublicRequest(originalRequest.url) && Boolean(originalRequest.headers?.Authorization)

    if (error.response.status === 401 && isAuthenticatedRequest) {
      await handleAuthFailure()
    }

    const code = data?.code ?? data?.error?.code ?? 'INTERNAL_ERROR'

    // Bypassing permissions after ceasing to be an administrator: the switch
    // goes off and the request is sent again, once, the ordinary way.
    if (
      code === 'permission_bypass_not_allowed' &&
      originalRequest.headers?.[PERMISSION_BYPASS_HEADER] !== undefined
    ) {
      usePermissionBypassStore.getState().setBypassing(false)
      delete originalRequest.headers[PERMISSION_BYPASS_HEADER]
      return apiClient(originalRequest)
    }

    // The server's own sentence when it sent one; otherwise one for the status,
    // never the HTTP library's "Request failed with status code 500".
    const serverMessage =
      (typeof data?.detail === 'string' && data.detail) || data?.error?.message || null
    const message =
      serverMessage && !UNHELPFUL_SERVER_MESSAGES.has(serverMessage)
        ? serverMessage
        : messageForStatus(error.response.status)

    const apiError = new Error(message) as ApiError
    apiError.code = code
    apiError.status = error.response.status
    // The id support can look the failure up by, and facts the caller can act
    // on (where in a formula an error is), when the server sent them.
    if (typeof data?.error_id === 'string') apiError.errorId = data.error_id
    if (data?.details && typeof data.details === 'object') apiError.details = data.details

    return Promise.reject(apiError)
  }
)
